import { useEffect, useState } from 'react';

import { getPhotoStorageUsage, getPhotoCount } from '@/services/photo-service';

interface StorageUsageIndicatorProps {
  userId: string;
  /** When true, renders a 3px progress bar above the text (default: false). */
  showBar?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Displays total photo storage used and, when supported, the browser quota.
 * Usage data is read once on mount — refreshes automatically when userId changes.
 *
 * When `showBar={true}`, renders a 3px progress bar above the text.
 * Existing callers that omit `showBar` see no bar (default: false).
 */
export function StorageUsageIndicator({ userId, showBar = false }: StorageUsageIndicatorProps) {
  const [usageBytes, setUsageBytes] = useState<number | null>(null);
  const [photoCount, setPhotoCount] = useState<number | null>(null);
  const [quotaBytes, setQuotaBytes] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [usage, count] = await Promise.all([
          getPhotoStorageUsage(userId),
          getPhotoCount(userId),
        ]);
        if (!cancelled) {
          setUsageBytes(usage);
          setPhotoCount(count);
        }
      } catch (err) {
        console.error('[StorageUsageIndicator] Failed to load usage:', err);
        if (!cancelled) setLoadError(true);
      }

      // navigator.storage.estimate() — best-effort quota awareness
      if (navigator.storage?.estimate) {
        try {
          const estimate = await navigator.storage.estimate();
          if (!cancelled && estimate.quota) {
            setQuotaBytes(estimate.quota);
          }
        } catch {
          // Not all browsers support storage estimation
        }
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [userId]);

  if (loadError) {
    return (
      <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="storage-usage">
        Photos: —
      </p>
    );
  }

  if (usageBytes === null || photoCount === null) return null;

  const pct = quotaBytes && quotaBytes > 0
    ? Math.min(100, (usageBytes / quotaBytes) * 100)
    : 0;

  return (
    <div>
      {/* Progress bar — only when showBar=true and quotaBytes is known */}
      {showBar && quotaBytes !== null && quotaBytes > 0 && (
        <div className="w-full h-[3px] bg-[#2d2a26] rounded-full mb-1 overflow-hidden">
          <div
            data-testid="storage-gauge-bar"
            data-fill-pct={Math.round(pct)}
            className="h-full bg-[var(--color-accent)] rounded-full transition-[width] duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="storage-usage">
        Photos: {formatBytes(usageBytes)} ({photoCount} photo{photoCount !== 1 ? 's' : ''})
        {quotaBytes !== null && (
          <> of ~{formatBytes(quotaBytes)} available</>
        )}
      </p>
    </div>
  );
}
